import { useState } from "react";
import { X, HelpCircle, ShieldCheck, Eye } from "lucide-react";
import { AVI_SUBTYPES } from "@/components/icons/AviSubtypeIcons";

/**
 * AviOnboardingHint — carte d'aide compacte affichée UNE fois par
 * utilisateur (spec rev-avi-2 bis, Feb 2026). Explique en un coup d'œil
 * les 3 sous-types + le rôle de « Doit valider » vs « Témoin ».
 *
 * Le clic sur « J'ai compris » persiste la fermeture dans localStorage
 * (clé `avi-hint-dismissed`) — plus jamais affichée pour cet utilisateur
 * (par navigateur). Un lien discret « Revoir » permet de réafficher.
 */
const STORAGE_KEY = "avi-hint-dismissed";

export function shouldShowAviHint() {
  try { return localStorage.getItem(STORAGE_KEY) !== "1"; }
  catch { return true; }
}

export default function AviOnboardingHint({ onDismiss }) {
  const [visible, setVisible] = useState(true);
  if (!visible) return null;
  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
    setVisible(false);
    onDismiss?.();
  };
  return (
    <div
      className="rounded-xl p-4 mb-5 relative"
      style={{
        background: "linear-gradient(135deg, rgba(236,72,153,0.08), rgba(132,204,22,0.06))",
        border: "1px solid rgba(236,72,153,0.28)",
      }}
      data-testid="avi-onboarding-hint"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-3 right-3 text-muted-foreground hover:text-white"
        data-testid="avi-hint-close"
        aria-label="Fermer"
      >
        <X size={16} />
      </button>
      <div className="flex items-center gap-2 mb-3">
        <HelpCircle size={16} style={{ color: "#EC4899" }} />
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#EC4899" }}>
          Bienvenue sur AVI
        </div>
      </div>
      <div className="text-[13px] leading-relaxed mb-3" style={{ color: "#D1D5DB" }}>
        Trois types de demandes, un seul endroit pour tout centraliser.
      </div>

      {/* Grille 3 types */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {Object.values(AVI_SUBTYPES).map((s) => {
          const Icon = s.Icon;
          const descByKey = {
            validation:  "Attendre une décision : approuvée ou refusée.",
            accuse:      "Prouver qu'un document a bien été reçu.",
            information: "Diffuser une info, sans exiger de réponse.",
          };
          return (
            <div
              key={s.key}
              className="rounded-lg p-3"
              style={{ background: "#161616", border: `1px solid ${s.color}44` }}
              data-testid={`avi-hint-type-${s.key}`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Icon size={18} />
                <span className="text-[13px] font-semibold" style={{ color: s.color }}>{s.label}</span>
              </div>
              <div className="text-[11px] leading-snug" style={{ color: "#9CA3AF" }}>
                {descByKey[s.key]}
              </div>
            </div>
          );
        })}
      </div>

      {/* Rôles */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="flex items-start gap-2 rounded-lg px-3 py-2"
             style={{ background: "#161616", border: "1px solid rgba(239,68,68,0.28)" }}>
          <ShieldCheck size={14} style={{ color: "#F87171", marginTop: 2 }} />
          <div>
            <div className="text-[12px] font-semibold" style={{ color: "#F87171" }}>Doit valider</div>
            <div className="text-[10.5px] leading-snug" style={{ color: "#9CA3AF" }}>
              Doit approuver ou refuser. Sa réponse compte dans le verdict global.
            </div>
          </div>
        </div>
        <div className="flex items-start gap-2 rounded-lg px-3 py-2"
             style={{ background: "#161616", border: "1px solid rgba(37,99,235,0.28)" }}>
          <Eye size={14} style={{ color: "#60A5FA", marginTop: 2 }} />
          <div>
            <div className="text-[12px] font-semibold" style={{ color: "#60A5FA" }}>Témoin</div>
            <div className="text-[10.5px] leading-snug" style={{ color: "#9CA3AF" }}>
              Simplement tenu informé. Aucune décision demandée.
            </div>
          </div>
        </div>
      </div>

      {/* Règle multi-décideurs + bouton */}
      <div className="flex items-center justify-between gap-3 pt-3"
           style={{ borderTop: "1px dashed #2A2A2A" }}>
        <div className="text-[11px] italic" style={{ color: "#9CA3AF" }}>
          Avec plusieurs décideurs : <b style={{ color: "#F87171" }}>1 refus bloque tout</b>, sinon <b style={{ color: "#4ADE80" }}>tous doivent approuver</b>.
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg text-[12px] font-semibold text-white transition-all"
          style={{ background: "#EC4899", boxShadow: "0 4px 12px rgba(236,72,153,0.35)" }}
          data-testid="avi-hint-dismiss"
        >
          J'ai compris
        </button>
      </div>
    </div>
  );
}
